import { Link } from "react-router-dom";
import { Target, Search, TrendingUp, ArrowUpRight, Check } from "lucide-react";
import { Section, Eyebrow, FadeIn } from "@/components/Section";
import useSEO from "@/hooks/useSEO";

const blocks = [
  {
    id: "meta-ads",
    icon: Target,
    name: "Meta Ads",
    tagline: "Facebook & Instagram performance built around CAC, ROAS and LTV.",
    description:
      "From first-touch awareness to remarketing, we engineer Meta funnels that scale predictably. Creative testing pipelines, CAPI tracking, and a focus on incremental revenue — not last-click illusions.",
    deliverables: [
      "Account audit & restructure",
      "Creative production (UGC, static, motion)",
      "CAPI / server-side tracking",
      "Catalogue + dynamic product ads",
      "Weekly experimentation cadence",
      "Monthly QBRs tied to revenue",
    ],
  },
  {
    id: "google-ads",
    icon: Search,
    name: "Google Ads",
    tagline:
      "Search, Performance Max, Shopping and YouTube — for high-intent capture.",
    description:
      "We treat Google as a demand-capture machine, not a brand budget. Tight match-type strategies, signal-fed PMax, shopping feed optimisation and YouTube for scaling beyond search ceilings.",
    deliverables: [
      "Search & PMax campaigns",
      "Shopping feed optimisation",
      "YouTube full-funnel video",
      "Conversion tracking & GA4",
      "Bid strategy & budget pacing",
      "Looker Studio dashboards",
    ],
  },
  {
    id: "seo",
    icon: TrendingUp,
    name: "SEO",
    tagline:
      "Technical, content and authority strategy to compound organic revenue.",
    description:
      "We don't chase keywords — we win topical territory. Technical foundations, EEAT-grade content production and a digital PR engine that earns the links your competitors can't buy.",
    deliverables: [
      "Technical SEO audit",
      "Information architecture & topical maps",
      "Content production & briefs",
      "On-page optimisation",
      "Digital PR & link building",
      "Quarterly forecasting & reporting",
    ],
  },
];

export default function Services() {
  useSEO({
    title: "Services · Meta Ads, Google Ads & SEO",
    description:
      "Performance marketing services for European brands: Meta Ads, Google Ads (Search · PMax · Shopping · YouTube) and SEO (technical · content · digital PR). Built around revenue.",
    path: "/services",
  });
  return (
    <div data-testid="page-services">
      <Section className="pt-36">
        <FadeIn>
          <Eyebrow>Services</Eyebrow>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h1 className="font-display mt-5 text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.92] max-w-5xl">
            Three channels.
            <br />
            <span className="text-[#ccff00]">Built to compound.</span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-2xl">
            We're a focused team. We don't do every digital channel — only the
            three that move the needle for product, ecommerce and SaaS brands
            scaling across Europe.
          </p>
        </FadeIn>
      </Section>

      {blocks.map((b, i) => (
        <Section
          key={b.id}
          id={b.id}
          dataTestid={`service-${b.id}`}
          className={`border-t border-white/10 ${
            i % 2 === 1 ? "bg-[#0a0a0b]" : ""
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <FadeIn>
                <div className="flex items-center gap-4 mb-6">
                  <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-3">
                    <b.icon className="h-6 w-6 text-[#ccff00]" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    0{i + 1} / 03
                  </span>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
                  {b.name}
                </h2>
                <p className="mt-4 text-[#ccff00] font-medium leading-relaxed">
                  {b.tagline}
                </p>
                <p className="mt-5 text-zinc-400 leading-relaxed">
                  {b.description}
                </p>
                <Link
                  to="/contact"
                  data-testid={`service-${b.id}-cta`}
                  className="mt-8 inline-flex items-center gap-2 bg-[#ccff00] hover:bg-[#d4ff33] text-black px-5 py-3 font-bold tracking-tight transition group"
                >
                  Discuss your {b.name} strategy
                  <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </FadeIn>
            </div>

            <div className="md:col-span-7">
              <FadeIn delay={0.1}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-5">
                  What you get
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                  {b.deliverables.map((d) => (
                    <li
                      key={d}
                      className="bg-[#0f0f10] hover:bg-[#141416] p-5 flex items-start gap-3 transition"
                    >
                      <Check className="h-4 w-4 text-[#ccff00] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-200 leading-relaxed">
                        {d}
                      </span>
                    </li>
                  ))}
                </ul>
              </FadeIn>
            </div>
          </div>
        </Section>
      ))}

      {/* CTA block */}
      <Section className="border-t border-white/10">
        <FadeIn>
          <div className="border border-white/10 bg-[#0f0f10] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <Eyebrow>Not sure where to start?</Eyebrow>
              <h2 className="font-display mt-4 text-3xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
                Book a free strategy call.
                <br />
                <span className="text-[#ccff00]">We'll tell you the truth.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <Link
                to="/contact"
                data-testid="services-final-cta"
                className="inline-flex items-center gap-2 bg-[#ccff00] hover:bg-[#d4ff33] text-black px-6 py-4 font-bold tracking-tight transition group"
              >
                Book a call
                <ArrowUpRight className="h-5 w-5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
}
